import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FileText, Loader2, Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "@/shared/i18n";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useCreatorReceiptRequests } from "../model/hooks";
import { MockBanner } from "../ui/MockBanner";
import type { CreatorReceiptRequestStatus } from "../types";

const PAGE_SIZE = 10;

const formatMonthShort = (paymentMonth: string) => {
  const [y, m] = paymentMonth.split("-");
  if (!y || !m) return paymentMonth;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return format(d, "MMM/yyyy", { locale: ptBR });
};

const formatMonths = (months: string[]) =>
  months.map(formatMonthShort).join(", ");

const currency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);

const statusLabelKeys: Record<
  CreatorReceiptRequestStatus,
  string
> = {
  PENDING: "paymentRequests.statusPending",
  PROCESSING: "paymentRequests.statusProcessing",
  PAID: "paymentRequests.statusPaid",
  REJECTED: "paymentRequests.statusRejected",
};

export function SolicitacoesPagamentoPage() {
  const { t } = useTranslation("creator-financial");
  const [page, setPage] = useState(1);
  const {
    requests,
    page: currentPage,
    limit,
    totalItems,
    totalPages,
    isLoading,
  } = useCreatorReceiptRequests({ page, limit: PAGE_SIZE });

  const from = totalItems === 0 ? 0 : (currentPage - 1) * limit + 1;
  const to = Math.min(currentPage * limit, totalItems);

  return (
    <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
      <MockBanner />
      <div>
        <h1 className="cf-page-title">{t("paymentRequests.title")}</h1>
        <p className="cf-page-subtitle">{t("paymentRequests.subtitle")}</p>
      </div>

      <div className="cf-card">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-[var(--cf-text-muted)] mb-4" />
            <p className="text-sm text-[var(--cf-text-muted)]">
              {t("paymentRequests.loading")}
            </p>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-[var(--cf-text-muted)] mb-4" />
            <p className="text-[var(--cf-text)] font-medium mb-1">
              {t("paymentRequests.emptyTitle")}
            </p>
            <p className="text-sm text-[var(--cf-text-muted)] max-w-md">
              {t("paymentRequests.emptyDescription")}
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--cf-border)] hover:bg-transparent">
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.columnStatus")}
                  </TableHead>
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.columnMonths")}
                  </TableHead>
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.columnAmount")}
                  </TableHead>
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.columnBank")}
                  </TableHead>
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.documentNfe")}
                  </TableHead>
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.documentReceipt")}
                  </TableHead>
                  <TableHead className="text-[var(--cf-text-muted)]">
                    {t("paymentRequests.columnCreatedAt")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <TableRow
                    key={req.id}
                    className="border-[var(--cf-border)]"
                  >
                    <TableCell>
                      <Badge
                        variant={
                          req.status === "PAID"
                            ? "default"
                            : req.status === "REJECTED"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {t(statusLabelKeys[req.status])}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[var(--cf-text)]">
                      {formatMonths(req.paymentMonths)}
                    </TableCell>
                    <TableCell className="text-[var(--cf-text)]">
                      {req.totalAmount != null
                        ? currency(req.totalAmount)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-[var(--cf-text)]">
                      {req.bankAccount.bank} — {req.bankAccount.account}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-[var(--cf-text)]"
                        onClick={() =>
                          req.nfeFileUrl &&
                          window.open(req.nfeFileUrl, "_blank")
                        }
                        disabled={!req.nfeFileUrl}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        {t("paymentRequests.documentNfe")}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-[var(--cf-text)]"
                        onClick={() =>
                          req.receiptFileUrl &&
                          window.open(req.receiptFileUrl!, "_blank")
                        }
                        disabled={!req.receiptFileUrl}
                      >
                        <Receipt className="h-4 w-4 mr-1" />
                        {t("paymentRequests.documentReceipt")}
                      </Button>
                    </TableCell>
                    <TableCell className="text-[var(--cf-text-muted)] text-sm">
                      {format(
                        new Date(req.createdAt),
                        "dd/MM/yyyy HH:mm",
                        { locale: ptBR }
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[var(--cf-text-muted)]">
                {t("paymentRequests.paginationSummary", {
                  from,
                  to,
                  total: totalItems,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[var(--cf-border)] text-[var(--cf-text)]"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t("paymentRequests.previous")}
                </Button>
                <span className="text-sm text-[var(--cf-text-muted)] px-2">
                  {t("paymentRequests.pageOf", {
                    current: page,
                    total: Math.max(1, totalPages),
                  })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[var(--cf-border)] text-[var(--cf-text)]"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("paymentRequests.next")}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
